<template>
  <div
    ref="timelineItemCard"
    class="timeline-item-card position"
    :class="{
      'no-start-date': noStartDateClass,
      'no-end-date': noEndDateClass,
      'over-due': addOverDueClass,
      'end-date-before-start-date': endDateIsBeforeStartDate,
      'card-completed': isCardCompleted,
    }"
    :style="cardStyle"
    @mouseenter="cardMouseEnter"
    @mouseleave="cardMouseLeave"
  >
    <StInfoIcon
      v-if="showInfoIcon"
      class="timeline-info-icon"
      :icon="'alert-circle'"
      :icon-color="infoIconColor"
      :tooltip-text="infoIconContent"
      :disable-tooltip="resizeStarted"
    />
    <div
      v-if="!isGuestReader"
      ref="leftResizer"
      class="timeline-card-resizer left"
      :class="{ active: resizingActive === 'left', disabled: resizingActive === 'right' }"
      @mousedown="leftResizerMouseDown"
      @touchstart="leftResizerTouchStart"
    />
    <div
      class="item-card-content-wrap pt"
      @click="cardClick"
      @mousedown="cardMouseDown"
      @touchstart="cardTouchStart"
    >
      <!-- Add transform translateX style on item-card-title-wrap to move elements -->
      <div
        class="item-card-title-wrap"
        :class="cardTitleClass"
      >
        <div class="item-card-title">
          {{ card.title }}
        </div>
      </div>
    </div>
    <div
      v-if="!isGuestReader"
      ref="rightResizer"
      class="timeline-card-resizer right"
      :class="{ active: resizingActive === 'right', disabled: resizingActive === 'left' }"
      @mousedown="rightResizerMouseDown"
      @touchstart="rightResizerTouchStart"
    />
  </div>
</template>

<script>
import boardListCardMixin from '@/mixins/boardListCardMixin';
import styleVariables from '@/assets/scss/variables.module.scss';
import { convertStyleToNumber } from '@/utilities/rootStyles';
import timelineDragMixin from '@/mixins/timelineDragMixin';
import timelineMixin from '@/mixins/timelineMixin';
import dueDateMixin from '@/mixins/dueDateMixin';
import cardStatuses from '@/mixins/cardStatuses';
import differenceInDays from 'date-fns/differenceInDays';
import addDays from 'date-fns/addDays';
import getUnixTime from 'date-fns/getUnixTime';
import format from 'date-fns/format';
import roundUpToNearestMultiple from '@/utilities/roundUpToNearestMultiple';
import tippy, { followCursor } from 'tippy.js';
import StInfoIcon from '@/components/widgets/StInfoIcon.vue';
import useCurrentRole from '@/utilities/composables/useCurrentRole';

export default {
  name: 'TimelineListCard',
  components: {
    StInfoIcon,
  },
  mixins: [boardListCardMixin, timelineDragMixin, timelineMixin, dueDateMixin, cardStatuses],
  props: {
    cardId: {
      type: String,
      required: true,
    },
    leftPosition: {
      type: Number,
      required: false,
      default: 0,
    },
    rightPosition: {
      type: Number,
      required: false,
      default: 0,
    },
    width: {
      type: Number,
      required: false,
      default: 0,
    },
    isStartDateSet: {
      type: Boolean,
      required: false,
      default: false,
    },
    isEndDateSet: {
      type: Boolean,
      required: false,
      default: false,
    },
    isOverDue: {
      type: Boolean,
      required: false,
      default: false,
    },
    startDate: {
      type: Number,
      required: false,
      default: 0,
    },
    endDate: {
      type: Number,
      required: false,
      default: 0,
    },
    endDateIsBeforeStartDate: {
      type: Boolean,
      required: false,
      default: false,
    },
    position: {
      type: Number,
      required: false,
      default: 0,
    },
    containerWidth: {
      type: Number,
      required: false,
      default: 0,
    },
    listCards: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  setup() {
    const { isGuestReader } = useCurrentRole();

    return {
      isGuestReader,
    };
  },

  data() {
    return {
      initialPosition: 0,
      initialWidth: 0,
      initialLeftPosition: 0,
      initialRightPosition: 0,
      resizingWidth: 0,
      deltaDays: 0,
      resizingActive: '',
      resizeStarted: false,
      preventOpenCard: false,
      cardHovered: false,
      tooltip: null,
    };
  },
  computed: {
    cards() {
      return this.$store.getters.getCards || {};
    },
    card() {
      return this.cards[this.cardId];
    },
    cardStyle() {
      const scrollOffset = convertStyleToNumber(styleVariables.timelineScrollbarWidth);
      const {
        initialLeftPosition,
        leftPosition,
        isStartDateSet,
        initialRightPosition,
        rightPosition,
        isEndDateSet,
        resizingWidth,
        width,
        isCardStarted,
        isStartDateAfterToday,
        cardDragActive,
        rightSideResizeActive,
      } = this;
      return {
        ...((initialLeftPosition || leftPosition) &&
          isStartDateSet && { left: `${initialLeftPosition || leftPosition}px` }),
        ...((initialRightPosition || rightPosition) &&
          !isStartDateSet && {
            right: `${(initialRightPosition || rightPosition) - scrollOffset}px`,
          }),
        ...((resizingWidth || width) &&
          ((isStartDateSet && isEndDateSet) ||
            (initialRightPosition && !isEndDateSet) ||
            (initialLeftPosition && !isStartDateSet) ||
            (isCardStarted &&
              !isStartDateAfterToday &&
              !cardDragActive &&
              !rightSideResizeActive)) && {
            width: `${resizingWidth || width}px`,
          }),
      };
    },
    isCardStarted() {
      return this.card.status === this.cardStatuses.started.id;
    },
    isCardCompleted() {
      return this.card.status === this.cardStatuses.completed.id;
    },
    isCardOverDue() {
      const {
        resizingWidth,
        isOverDue,
        resizingActive,
        initialLeftPosition,
        todayPosition,
        leftPosition,
      } = this;
      if (!resizingWidth) return isOverDue;
      if (resizingActive === 'both') return initialLeftPosition + resizingWidth <= todayPosition;
      return resizingActive === 'left'
        ? initialLeftPosition + resizingWidth <= todayPosition
        : leftPosition + resizingWidth <= todayPosition;
    },
    addOverDueClass() {
      const {
        leftSideResizeActiveWithoutEndDate,
        rightSideResizeActiveWithoutStartDate,
        isCardOverDue,
        isCardCompleted,
        endDateIsBeforeStartDate,
        isStartDateSet,
        isEndDateSet,
        resizeStarted,
      } = this;
      if (leftSideResizeActiveWithoutEndDate || rightSideResizeActiveWithoutStartDate) return false;
      return (
        isCardOverDue &&
        !isCardCompleted &&
        !endDateIsBeforeStartDate &&
        ((isStartDateSet && isEndDateSet) || resizeStarted)
      );
    },
    leftSideResizeActive() {
      return this.resizeStarted && this.resizingActive === 'left';
    },
    rightSideResizeActive() {
      return this.resizeStarted && this.resizingActive === 'right';
    },
    cardDragActive() {
      return this.resizeStarted && this.resizingActive === 'both';
    },
    leftSideResizeActiveWithoutEndDate() {
      return (this.leftSideResizeActive || this.cardDragActive) && !this.isEndDateSet;
    },
    rightSideResizeActiveWithoutStartDate() {
      return (this.rightSideResizeActive || this.cardDragActive) && !this.isStartDateSet;
    },
    noStartDateClass() {
      return !this.isStartDateSet && !this.leftSideResizeActive;
    },
    noEndDateClass() {
      return !this.isEndDateSet && !this.rightSideResizeActive;
    },
    parentCardWidth() {
      return this.resizingWidth || this.width;
    },
    cardTitleWrapPadding() {
      return convertStyleToNumber(styleVariables.timelineCardTitlePaddingX) * 2;
    },
    cardTitleWidth() {
      return this.parentCardWidth - this.cardTitleWrapPadding;
    },
    cardTitleClass() {
      return this.cardTitleWidth <= this.$constants.timelineCardTitleMinWidth
        ? ''
        : 'overflow-hidden';
    },
    isStartDateAfterToday() {
      return (this.initialLeftPosition || this.leftPosition) >= this.todayPosition;
    },
    leftResizerTooltipContent() {
      return format(
        addDays(this.startDate, this.deltaDays),
        this.$constants.timelineTooltipDateFormat
      );
    },
    rightResizerTooltipContent() {
      return format(
        addDays(this.endDate, this.deltaDays),
        this.$constants.timelineTooltipDateFormat
      );
    },
    bothDatesTooltipContent() {
      return `${this.leftResizerTooltipContent} - ${this.rightResizerTooltipContent}`;
    },
    cardMoveTooltipContent() {
      if (this.isStartDateSet && this.isEndDateSet) return this.bothDatesTooltipContent;
      return this.isStartDateSet ? this.leftResizerTooltipContent : this.rightResizerTooltipContent;
    },
    tooltipContent() {
      if (this.cardDragActive) return this.cardMoveTooltipContent;
      return this.leftSideResizeActive
        ? this.leftResizerTooltipContent
        : this.rightResizerTooltipContent;
    },
    showInfoIcon() {
      return this.addOverDueClass || this.endDateIsBeforeStartDate;
    },
    infoIconContent() {
      if (this.endDateIsBeforeStartDate) return this.translate('endDateIsBeforeStartDate');
      return this.isCardOverDue ? this.translate('taskIsOverdue') : '';
    },
    infoIconColor() {
      return this.endDateIsBeforeStartDate ? styleVariables.warning400 : styleVariables.error400;
    },
  },
  watch: {
    resizeStarted(val) {
      if (val && this.tooltip) {
        this.tooltip.show();
      }
    },
    tooltipContent(newContent) {
      if (this.tooltip) {
        this.tooltip.setProps?.({ content: newContent });
      }
    },
  },
  beforeUnmount() {
    this.removeEvents();
    this.destroyTooltip();
  },
  methods: {
    initializeTooltip() {
      this.tooltip = tippy(this.$refs.timelineItemCard, {
        content: this.tooltipContent,
        trigger: 'manual',
        arrow: false,
        hideOnClick: false,
        duration: 0,
        followCursor: 'horizontal',
        plugins: [followCursor],
      });
    },
    initializeResizeData(eventType, clientX, target) {
      this.start(eventType, clientX);
      this.initialPosition = this.position;
      const { width } = this.$refs.timelineItemCard.getBoundingClientRect();
      this.initialWidth =
        width % this.dayWidth === 0
          ? width
          : roundUpToNearestMultiple(width, this.dayWidth) + this.dayWidth;
      this.resizingActive = target;
      if (target === 'left') {
        this.initialLeftPosition = this.leftPosition;
      } else if (target === 'right') {
        this.initialRightPosition = this.rightPosition;
      }
      if (eventType === 'mouse') {
        document.addEventListener('mouseup', this.handleResizeEnd);
        document.addEventListener('keydown', this.handleResizerKeyDown);
      } else {
        document.addEventListener('touchend', this.handleResizeEnd);
      }
    },
    leftResizerMouseDown(event) {
      if (!event) return;
      this.initializeResizeData('mouse', event.clientX, 'left');
      document.addEventListener('mousemove', this.leftResizerMouseMove);
    },
    rightResizerMouseDown(event) {
      if (!event) return;
      this.initializeResizeData('mouse', event.clientX, 'right');
      document.addEventListener('mousemove', this.rightResizerMouseMove);
    },
    cardMouseDown(event) {
      if (!event || this.isGuestReader) return;
      this.initializeResizeData('mouse', event.clientX, 'both');
      document.addEventListener('mousemove', this.cardMouseMove);
    },
    leftResizerTouchStart(event) {
      if (!event) return;
      const touch = event.touches[0];
      this.initializeResizeData('touch', touch.clientX, 'left');
      document.addEventListener('touchmove', this.handleLeftResizerTouchMove);
    },
    rightResizerTouchStart(event) {
      if (!event) return;
      const touch = event.touches[0];
      this.initializeResizeData('touch', touch.clientX, 'right');
      document.addEventListener('touchmove', this.handleRightResizerTouchMove);
    },
    cardTouchStart(event) {
      if (!event || this.isGuestReader) return;
      const touch = event.touches[0];
      this.initializeResizeData('touch', touch.clientX, 'both');
      document.addEventListener('touchmove', this.handleCardTouchMove);
    },
    leftResizerMouseMove(event) {
      if (!event) return;
      this.handleResize(event.clientX, 'left');
    },
    rightResizerMouseMove(event) {
      if (!event) return;
      this.handleResize(event.clientX, 'right');
    },
    cardMouseMove(event) {
      if (!event) return;
      this.handleResize(event.clientX, 'both');
    },
    handleLeftResizerTouchMove(event) {
      if (!event) return;
      const touch = event.touches[0];
      this.handleResize(touch.clientX, 'left');
    },
    handleRightResizerTouchMove(event) {
      if (!event) return;
      const touch = event.touches[0];
      this.handleResize(touch.clientX, 'right');
    },
    handleCardTouchMove(event) {
      if (!event) return;
      const touch = event.touches[0];
      this.handleResize(touch.clientX, 'both');
    },
    handleResize(clientX, target) {
      this.resizeStarted = true;
      const clientXOffset = clientX - this.initialClientX;
      const positionOffset = this.position - this.initialPosition;
      const calculatedOffset = clientXOffset + positionOffset;
      const daysOffset = Math.round(calculatedOffset / this.dayWidth);
      const totalDays = differenceInDays(this.startDate, this.endDate) + 1;
      switch (target) {
        case 'left':
          this.deltaDays = this.isEndDateSet ? Math.min(-totalDays, daysOffset) : daysOffset;
          this.resizingWidth = this.initialWidth - this.deltaDays * this.dayWidth;
          this.initialLeftPosition = this.leftPosition + this.deltaDays * this.dayWidth;
          break;
        case 'right':
          this.deltaDays = this.isStartDateSet ? Math.max(totalDays, daysOffset) : daysOffset;
          this.resizingWidth = this.initialWidth + this.deltaDays * this.dayWidth;
          this.initialRightPosition = this.rightPosition - this.deltaDays * this.dayWidth;
          break;
        case 'both':
          if (daysOffset !== 0) {
            this.preventOpenCard = this.cardHovered;
          }
          this.deltaDays = daysOffset;
          this.resizingWidth = this.initialWidth;
          this.initialLeftPosition = this.isStartDateSet
            ? this.leftPosition + this.deltaDays * this.dayWidth
            : 0;
          this.initialRightPosition = this.isEndDateSet
            ? this.rightPosition - this.deltaDays * this.dayWidth
            : 0;
          break;
        default:
          break;
      }
    },
    handleDateSet() {
      if (this.deltaDays === 0) return;
      if (this.resizingActive === 'both') {
        const card = {
          ...(this.isStartDateSet && {
            start_date: getUnixTime(addDays(this.startDate, this.deltaDays)),
          }),
          ...(this.isEndDateSet && {
            due_date: getUnixTime(addDays(this.endDate, this.deltaDays)),
          }),
        };
        this.$store.dispatch('updateCardWithPatchRequest', {
          cardId: this.cardId,
          card,
        });
        this.deltaDays = 0;
        return;
      }
      let dateProperty = 'start_date';
      let fromDate = this.startDate;
      if (this.resizingActive === 'right') {
        dateProperty = 'due_date';
        fromDate = this.isCardStarted && !this.isEndDateSet ? this.today.date : this.endDate;
      }
      const addedUnixTimestamp = getUnixTime(addDays(fromDate, this.deltaDays));
      this.setCardDate(addedUnixTimestamp, '', dateProperty, this.cardId);
      this.deltaDays = 0;
    },
    handleResizeEnd() {
      this.handleDateSet();
      this.removeEvents();
      this.setToDefaultData();
      this.hideTooltip();
      this.end();
    },
    handleResizerKeyDown(event) {
      if (this.isEscapeButton(event)) {
        event.stopPropagation();
        this.deltaDays = 0;
        this.handleResizeEnd();
      }
    },
    isEscapeButton(event) {
      return event.keyCode === 27 || event.key === 'Escape';
    },
    removeEvents() {
      if (this.pointerType === 'touch') {
        this.removeTouchEvents();
        return;
      }
      this.removeMouseEvents();
    },
    removeTouchEvents() {
      switch (this.resizingActive) {
        case 'left':
          document.removeEventListener('touchmove', this.handleLeftResizerTouchMove);
          break;
        case 'right':
          document.removeEventListener('touchmove', this.handleRightResizerTouchMove);
          break;
        case 'both':
          document.removeEventListener('touchmove', this.handleCardTouchMove);
          break;
        default:
          break;
      }
      document.removeEventListener('touchend', this.handleResizeEnd);
    },
    removeMouseEvents() {
      switch (this.resizingActive) {
        case 'left':
          document.removeEventListener('mousemove', this.leftResizerMouseMove);
          break;
        case 'right':
          document.removeEventListener('mousemove', this.rightResizerMouseMove);
          break;
        case 'both':
          document.removeEventListener('mousemove', this.cardMouseMove);
          break;
        default:
          break;
      }
      document.removeEventListener('mouseup', this.handleResizeEnd);
      document.removeEventListener('keydown', this.handleResizerKeyDown);
    },
    setToDefaultData() {
      this.resizingActive = '';
      this.resizeStarted = false;
      this.initialPosition = 0;
      this.resizingWidth = 0;
      this.initialLeftPosition = 0;
      this.initialRightPosition = 0;
      this.initialClientX = 0;
      this.initialWidth = 0;
    },
    cardMouseEnter() {
      this.cardHovered = true;
      if (this.tooltip) return;
      this.initializeTooltip();
    },
    cardMouseLeave() {
      this.cardHovered = false;
    },
    hideTooltip() {
      if (this.tooltip) {
        this.tooltip.hide();
      }
    },
    destroyTooltip() {
      if (this.tooltip) {
        this.tooltip.destroy();
        this.tooltip = null;
      }
    },
  },
};
</script>
